// DEPENDENCIES
import { useState, useEffect } from "react";

// CSS
import "./TanStackTable.css";

// CONSTANTS
import { RECORDS_PER_PAGE } from "../../../constants/constants";

// TANSTACK TABLE COMPONENTS
import {
	useReactTable,
	getCoreRowModel,
	flexRender,
	getPaginationRowModel,
	getFilteredRowModel,
	getSortedRowModel,
} from "@tanstack/react-table";

// MUI IMPORTS
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from "@mui/material";

// MUI ICONS
import {
	UnfoldMore as UnfoldMoreIcon,
	ExpandLess as ExpandLessIcon,
	ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";

// LOCAL CONSTANTS
import TanStackTableFilter from "./TanStackTableFilter/TanStackTableFilter";
import TanStackTablePagination from "./TanStackTablePagination/TanStackTablePagination";

const TanStackTable = (props) => {
	// STATES
	const [data, setData] = useState(props.rowData || []);
	const [columns, setColumns] = useState(props.columnData || []);
	const [internalPagination, setInternalPagination] = useState({
		pageIndex: 0,
		pageSize: RECORDS_PER_PAGE,
	});
	const [internalColumnFilters, setInternalColumnFilters] = useState([]);
	const [internalSorting, setInternalSorting] = useState(
		props.defaultSort || []
	);

	// When the parent owns pagination/filters/sorting (e.g. server-driven tables),
	// it passes these down as controlled props; otherwise fall back to local state.
	const pagination = props.pagination ?? internalPagination;
	const setPagination = props.onPaginationChange ?? setInternalPagination;
	const columnFilters = props.columnFilters ?? internalColumnFilters;
	const setColumnFilters =
		props.onColumnFiltersChange ?? setInternalColumnFilters;
	const sorting = props.sorting ?? internalSorting;
	const setSorting = props.onSortingChange ?? setInternalSorting;

	const manualPagination = !!props.manualPagination;
	const manualFiltering = !!props.manualFiltering;
	const manualSorting = !!props.manualSorting;

	// Sync props → state if they ever change
	useEffect(() => {
		if (props.rowData) setData(props.rowData);
		if (props.columnData) setColumns(props.columnData);
	}, [props.rowData, props.columnData]);

	// TABLE SETUP
	const table = useReactTable({
		data,
		columns,
		state: {
			pagination,
			columnFilters,
			sorting,
		},
		onPaginationChange: setPagination,
		onColumnFiltersChange: setColumnFilters,
		onSortingChange: setSorting,
		getCoreRowModel: getCoreRowModel(),
		...(manualPagination ? {} : { getPaginationRowModel: getPaginationRowModel() }),
		...(manualFiltering ? {} : { getFilteredRowModel: getFilteredRowModel() }),
		...(manualSorting ? {} : { getSortedRowModel: getSortedRowModel() }),
		manualPagination,
		manualFiltering,
		manualSorting,
		pageCount: manualPagination ? props.pageCount ?? -1 : undefined,
		columnResizeMode: "onChange",
		enableColumnResizing: true,
	});

	// RENDER
	return (
		<div className="tanStackTable">
			{/* Tanstack Table Filter and Popover Element */}
			{props.showSearch != false && (
				<TanStackTableFilter
					columns={columns}
					columnFilters={columnFilters}
					setColumnFilters={setColumnFilters}
					setFilterResetter={props.setFilterResetter}
				/>
			)}
			{/* End Tanstack Table Filter and Popover */}
			{/* Tanstack Table */}
			<TableContainer id="tanStackTableContainer">
				<Table id="tanStackTable" stickyHeader>
					{/* Table Header */}
					<TableHead>
						{table.getHeaderGroups().map((headerGroup) => (
							<TableRow key={headerGroup.id}>
								{headerGroup.headers.map((header) => (
									<TableCell
										id="tanStackTableHeaderCell"
										key={header.id}
										sx={{ width: header.getSize() }}
										align="left"
									>
										<div id="tanStackTableHeaderCellContents">
											{!header.isPlaceholder && (
												<div
													id="tanStackTableHeaderLabel"
													style={{
														cursor: header.column.getCanSort()
															? "pointer"
															: "default",
													}}
													onClick={header.column.getToggleSortingHandler()}
												>
													{flexRender(
														header.column.columnDef
															.header,
														header.getContext()
													)}

													{header.column.getCanSort() &&
														({
															asc: (
																<ExpandLessIcon />
															),
															desc: (
																<ExpandMoreIcon />
															),
														}[
															header.column.getIsSorted()
														] ?? (
															<UnfoldMoreIcon />
														))}
												</div>
											)}
										</div>

										{/* Column resize handle */}
										{header.column.getCanResize() && (
											<div
												onMouseDown={header.getResizeHandler()}
												onTouchStart={header.getResizeHandler()}
												className={`resizer ${
													header.column.getIsResizing()
														? "isResizing"
														: ""
												}`}
											/>
										)}
									</TableCell>
								))}
							</TableRow>
						))}
					</TableHead>

					{/* Table Body */}
					<TableBody>
						{table.getRowModel().rows.length > 0 ? (
							table.getRowModel().rows.map((row) => (
								<TableRow key={row.id} className={props.getRowClassName?.(row.original)}>
									{row.getVisibleCells().map((cell) => (
										<TableCell
											id="tanStackTableRowCell"
											key={cell.id}
											sx={{
												width: cell.column.getSize(),
											}}
											align="left"
										>
											<div id="tanStackTableCellContents">
												{flexRender(
													cell.column.columnDef.cell,
													cell.getContext()
												)}
											</div>
										</TableCell>
									))}
								</TableRow>
							))
						) : (
							<TableRow>
								<TableCell
									align="center"
									colSpan={
										table.getHeaderGroups()[0]?.headers
											.length ?? columns.length
									}
									sx={{ py: 4 }}
								>
									<span id="tanStackTableNoResults">
										No Results Found
									</span>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>
			{/* End Table Body */}
			{/* End TanStack Table */}

			{/* Pagination */}
			<TanStackTablePagination
				table={table}
				pagination={pagination}
				disabled={props.isLoading}
			/>
			{/* End of Pagination */}
		</div>
	);
};

export default TanStackTable;
